import {
  FEEDBACK_ERROR_MESSAGE_MAX_LENGTH,
  FEEDBACK_ERROR_STACK_MAX_LENGTH,
  FEEDBACK_LAST_ERRORS_MAX,
  FeedbackClientError,
} from '@droidmap/user-service-contract'

/**
 * A circular buffer of the last few unhandled errors, so a report filed after
 * something visibly broke carries the stack trace that caused it. Without
 * this, "it crashed" arrives with nothing to act on.
 *
 * Installed at app bootstrap (see `init.ts`) rather than when the modal opens
 * — by the time someone reaches for the feedback button the error has already
 * happened.
 */
const buffer: FeedbackClientError[] = []

/** Guards against double-installing under React StrictMode / HMR. */
let installed = false

const truncate = (value: string, limit: number): string =>
  value.length <= limit ? value : `${value.slice(0, limit - 1)}…`

/**
 * Drop the query string from any URL in captured text.
 *
 * This buffer is posted verbatim into a Slack channel, and a rejected upload
 * or download promise routinely carries a presigned S3 URL whose query string
 * *is* the credential. The path is what makes an error legible; the query
 * string is what makes it a leak.
 */
const stripUrlQueryStrings = (value: string): string =>
  value.replace(/(https?:\/\/[^\s'"`)]+?)\?[^\s'"`)]*/g, '$1?…')

/**
 * Normalise anything thrown — an `Error`, a string, a rejected promise's
 * arbitrary reason — into a loggable entry. Non-Errors are stringified rather
 * than dropped: `throw 'boom'` is still a real failure worth reporting.
 */
const toClientError = (
  thrown: unknown,
  fallback: string,
): FeedbackClientError => {
  const timestamp = new Date().toISOString()

  if (thrown instanceof Error) {
    return {
      message: truncate(
        stripUrlQueryStrings(thrown.message || fallback),
        FEEDBACK_ERROR_MESSAGE_MAX_LENGTH,
      ),
      ...(thrown.stack && {
        stack: truncate(
          stripUrlQueryStrings(thrown.stack),
          FEEDBACK_ERROR_STACK_MAX_LENGTH,
        ),
      }),
      timestamp,
    }
  }

  return {
    message: truncate(
      stripUrlQueryStrings(String(thrown ?? fallback)),
      FEEDBACK_ERROR_MESSAGE_MAX_LENGTH,
    ),
    timestamp,
  }
}

/**
 * Append an entry, evicting the oldest once the buffer is full. Exported so
 * an error boundary can contribute a React render error, which never reaches
 * `window.onerror`.
 */
export const recordClientError = (
  thrown: unknown,
  fallback = 'Unknown error',
): void => {
  buffer.push(toClientError(thrown, fallback))
  while (buffer.length > FEEDBACK_LAST_ERRORS_MAX) {
    buffer.shift()
  }
}

/** Oldest first, matching the order they occurred. */
export const getRecentClientErrors = (): FeedbackClientError[] => [...buffer]

/** Test seam — the buffer is module state and outlives a single test. */
export const clearRecentClientErrors = (): void => {
  buffer.length = 0
  installed = false
}

/**
 * Start capturing unhandled errors and promise rejections.
 *
 * Uses `addEventListener` rather than assigning `window.onerror` /
 * `window.onunhandledrejection`: those are single-slot properties, and
 * assigning them would silently unhook whatever else is listening (RUM, a
 * future error reporter). Returns a teardown so tests can uninstall.
 */
export const installErrorBuffer = (target: Window = window): (() => void) => {
  // A second caller gets a no-op teardown *and* is told so — handing back a
  // teardown that silently owns nothing would let a test uninstall a buffer
  // it never installed and then wonder why events still arrive.
  if (installed) {
    return () => undefined
  }
  installed = true

  const onError = (event: ErrorEvent) => {
    // `event.error` carries the stack; `event.message` is the only thing
    // available for cross-origin script errors ("Script error.").
    recordClientError(event.error ?? event.message, 'Unhandled error')
  }

  const onRejection = (event: PromiseRejectionEvent) => {
    recordClientError(event.reason, 'Unhandled promise rejection')
  }

  target.addEventListener('error', onError)
  target.addEventListener('unhandledrejection', onRejection)

  return () => {
    target.removeEventListener('error', onError)
    target.removeEventListener('unhandledrejection', onRejection)
    installed = false
  }
}
